import { t } from '@procumi/shared';
import { useState } from 'react';

/**
 * Avatarele: inițialele unui om sau ale unei firme, pe o culoare care rămâne
 * aceeași pentru același nume. Pe panou și în liste, furnizorii care au răspuns
 * stau suprapuși (cerut de proprietar, 2026-10-10); cei care n-au răspuns sunt
 * cercuri goale, ca diferența să nu stea doar în culoare.
 */

/** Culori stinse, toate cu text alb lizibil (contrast ≥4,5:1). */
const HUES = ['#2F7A5C', '#A9552A', '#5B48B8', '#3A5FA8', '#86622A', '#A6405F', '#2D6C80', '#5D6676'];

/** Cuvinte care nu spun nimic despre firmă și nu intră în inițiale. */
const SKIP = new Set(['srl', 'sa', 'sc', 'pfa', 'ii', 'snc', 'scs', 'impex', 'com', 'grup', 'group']);

export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .split(/[\s-]+/)
    .filter((w) => w && !SKIP.has(w.toLowerCase()));
  const pick = words.length ? words : name.split(/\s+/).filter(Boolean);
  const a = pick[0]?.[0] ?? '?';
  const b = pick.length > 1 ? pick[1]![0]! : (pick[0]?.[1] ?? '');
  return (a + b).toUpperCase();
}

function hue(name: string): string {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return HUES[h % HUES.length]!;
}

/** Pozele care n-au venit (șterse între timp): nu le mai cerem. */
const broken = new Set<string>();

/**
 * Avatarul: poza omului, dacă și-a pus una (`userId` + `avatarAt`), altfel
 * inițialele pe culoarea lui. Furnizorii n-au poze, doar inițiale.
 */
export function Avatar({
  name,
  size,
  userId,
  avatarAt,
}: {
  name: string;
  size?: 'lg' | 'xl';
  userId?: string | null;
  avatarAt?: string | null;
}) {
  const src = userId && avatarAt ? avatarUrl(userId, avatarAt) : null;
  const [failed, setFailed] = useState(() => (src ? broken.has(src) : false));
  return (
    <span
      className={`av${size ? ` ${size}` : ''}`}
      style={{ background: hue(name) }}
      title={name}
      aria-hidden="true"
    >
      {src && !failed ? (
        <img
          src={src}
          alt=""
          onError={() => {
            broken.add(src);
            setFailed(true);
          }}
        />
      ) : (
        initials(name)
      )}
    </span>
  );
}

/** Adresa pozei, cu versiunea în ea: o poză nouă are altă adresă. */
export const avatarUrl = (userId: string, avatarAt: string) =>
  `/api/profile/users/${encodeURIComponent(userId)}/avatar?v=${encodeURIComponent(avatarAt)}`;

/**
 * Furnizorii unei cereri, suprapuși: întâi cei care au răspuns, apoi cercurile
 * goale ale celor așteptați. Peste `max`, restul e un număr.
 */
export function SupplierStack({
  suppliers,
  max = 4,
}: {
  suppliers: { id: string; name: string; answered: boolean }[];
  max?: number;
}) {
  const sorted = [...suppliers].sort((a, b) => Number(b.answered) - Number(a.answered));
  const shown = sorted.slice(0, max);
  const answered = suppliers.filter((s) => s.answered).length;
  return (
    <span
      className="avs"
      title={sorted.map((s) => `${s.name}${s.answered ? t(' (a răspuns)') : t(' (așteptăm)')}`).join('\n')}
    >
      {shown.map((s) =>
        s.answered ? (
          <Avatar key={s.id} name={s.name} />
        ) : (
          <span key={s.id} className="av none" aria-hidden="true" />
        ),
      )}
      <span className="more">{t('{0} din {1}', answered, suppliers.length)}</span>
    </span>
  );
}
